import { useState, useMemo } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  List,
  Chip,
  Breadcrumbs,
  Link,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LockIcon from "@mui/icons-material/Lock";
import { useTheme } from "@mui/material/styles";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import SidebarMenu from "../components/SidebarMenu";
import DashboardFooter from "../components/DashboardFooter";
import { useAuth } from "../context/AuthContext";

const drawerWidth = 240;
const appBarHeight = 64;

/* ============================
   MENU CONFIG (UNCHANGED)
============================ */
const MENU_ITEMS = [
  { label: "Dashboard", to: "/dashboard", icon: "dashboard" },
  { label: "Subject Exam Practice", to: "/dashboard/subjects", icon: "exam" },
  { label: "Post UTME Practice", to: "/dashboard/post-utme-speed-test", icon: "exam" },
  { label: "JAMB Practice", to: "/dashboard/jamb-challenge", icon: "exam" },
  { label: "My Results", to: "/dashboard/results", icon: "results" },
  { label: "Subject Challenge", to: "/dashboard/weekly-subject-challenge", icon: "leaderboard" },
  { label: "POST UTME Challenge", to: "/dashboard/weekly-post-utme-challenge", icon: "leaderboard" },
  { label: "Performances", to: "/dashboard/my-rank", icon: "performance" },
  { label: "My Subscription", to: "/dashboard/my-subscriptions", icon: "wallet" },
  { label: "Settings", to: "/dashboard/settings", icon: "settings" },
  { label: "Logout", to: "/logout", icon: "logout" },
];

/* ============================
   PREMIUM ROUTES
============================ */
const PREMIUM_ROUTE_MATCHERS = [
  "/dashboard/subjects",
  "/dashboard/results",
  "/dashboard/result",
  "/dashboard/leaderboard",
  "/dashboard/post-utme-speed-test",
  "/dashboard/jamb-challenge",
];

/* ============================
   BREADCRUMB LABEL MAP
============================ */
const LABEL_MAP = {
  subjects: "Exam Page",
  results: "My Results",
  result: "Result Details",
  leaderboard: "Leaderboard",
  "my-rank": "My Rank",
  "my-subscriptions": "My Subscription",
  "post-utme-speed-test": "Post UTME",
  settings: "Settings",
};

export default function DashboardLayout() {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const displayName = user?.full_name || "Student";

  const toggleDrawer = () => setMobileOpen((p) => !p);

  /* ============================
     ACTIVE MENU RESOLVER
  ============================ */
  const resolveActivePath = (pathname, items) => {
    let bestMatch = "/dashboard";
    let bestLength = 0;

    const checkItem = (item) => {
      if (item.to && pathname.startsWith(item.to) && item.to.length > bestLength) {
        bestMatch = item.to;
        bestLength = item.to.length;
      }
      item.children?.forEach(checkItem);
    };

    items.forEach(checkItem);
    return bestMatch;
  };

  const activePath = useMemo(
    () => resolveActivePath(location.pathname, MENU_ITEMS),
    [location.pathname]
  );

  /* ============================
     BREADCRUMBS
  ============================ */
  const breadcrumbs = useMemo(() => {
    const parts = location.pathname
      .replace("/dashboard", "")
      .split("/")
      .filter(Boolean);

    const crumbs = [{ label: "Dashboard", to: "/dashboard" }];
    let currentPath = "/dashboard";

    parts.forEach((part) => {
      currentPath += `/${part}`;
      crumbs.push({
        label:
          LABEL_MAP[part] ||
          part.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        to: currentPath,
      });
    });

    return crumbs;
  }, [location.pathname]);

  /* ============================
     PREMIUM LOGIC
  ============================ */
  const isPremiumPage = PREMIUM_ROUTE_MATCHERS.some((p) =>
    location.pathname.startsWith(p)
  );

  const hasPremiumAccess =
    user?.has_active_subscription ||
    user?.has_active_cbt ||
    user?.is_trial_active;

  /* ============================
     DRAWER CONTENT
  ============================ */
  const drawer = (
    <>
      <Box sx={{ height: appBarHeight }} />
      <Divider />
      <List sx={{ p: 1 }}>
        <SidebarMenu
          items={MENU_ITEMS}
          activePath={activePath}
          onNavigate={(to) => {
            if (to === "/logout") {
              logout();
              return;
            }
            navigate(to);
            setMobileOpen(false);
          }}
        />
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />

      {/* ================= APP BAR ================= */}
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          height: appBarHeight,
          borderBottom: "1px solid",
          borderColor: "divider",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: appBarHeight }}>
          {!mdUp && (
            <IconButton onClick={toggleDrawer}>
              <MenuIcon />
            </IconButton>
          )}

          <Typography fontWeight={800}>
            CBT <span style={{ color: "#1976d2" }}>Master</span>
          </Typography>

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <AccountCircleIcon />
            <Typography variant="body2" noWrap>
              {displayName}
            </Typography>

            {user?.is_trial_active && (
              <Chip size="small" label="Trial" color="warning" />
            )}

            <IconButton onClick={logout}>
              <ExitToAppIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ================= DRAWER ================= */}
      <Drawer
        variant={mdUp ? "permanent" : "temporary"}
        open={mdUp || mobileOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* ================= MAIN ================= */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* APPBAR SPACER */}
        <Box sx={{ height: appBarHeight }} />

        {/* SCROLLABLE CONTENT */}
        <Box
          sx={{
            height: `calc(100vh - ${appBarHeight}px)`,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            p: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          {/* BREADCRUMBS */}
          <Breadcrumbs sx={{ mb: 2, flexWrap: "wrap" }}>
            {breadcrumbs.map((b, i) =>
              i === breadcrumbs.length - 1 ? (
                <Typography key={b.to} fontWeight={700}>
                  {b.label}
                </Typography>
              ) : (
                <Link
                  key={b.to}
                  underline="hover"
                  color="inherit"
                  onClick={() => navigate(b.to)}
                  sx={{ cursor: "pointer" }}
                >
                  {b.label}
                </Link>
              )
            )}
          </Breadcrumbs>

          {/* 🔒 PREMIUM BANNER */}
          {isPremiumPage && !hasPremiumAccess && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "warning.light",
                background:
                  "linear-gradient(90deg, rgba(255,193,7,0.12), rgba(255,152,0,0.08))",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                alignItems: { md: "center" },
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography fontWeight={800} color="warning.main">
                  🔒 Premium Feature
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This page is available to subscribed students. Upgrade to unlock
                  full access and advanced features.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip icon={<LockIcon />} label="Premium" color="warning" />

                <Typography
                  sx={{ cursor: "pointer", fontWeight: 700, color: "primary.main" }}
                  onClick={() => navigate("/dashboard/my-subscriptions")}
                >
                  Upgrade Now
                </Typography>

                <Typography
                  sx={{ cursor: "pointer", fontWeight: 600 }}
                  onClick={() => navigate("/dashboard/pricing")}
                >
                  View Plans
                </Typography>
              </Box>
            </Box>
          )}

          <Outlet />

          <DashboardFooter />
        </Box>
      </Box>
    </Box>
  );
}
